import React, { useState, useEffect } from "react";
import { sendMail } from "../../services/contact.service";

const ContactSection = () => {
    const [formData, setFormData] = useState({
        firstname: "",
        lastname: "",
        email_sender: "",
        organization: "",
        message: ""
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [showAlert, setShowAlert] = useState(false);

    // Effet pour gérer l'apparition et la disparition de l'alerte
    useEffect(() => {
        if (submitStatus) {
            setShowAlert(true);
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 5000); // L'alerte disparaît après 5 secondes
            return () => clearTimeout(timer);
        }
    }, [submitStatus]);

    const validateForm = () => {
        const newErrors = {};

        // Validation du nom
        if (!formData.lastname.trim()) {
            newErrors.lastname = "Le nom est requis";
        }

        // Validation du prénom
        if (!formData.firstname.trim()) {
            newErrors.firstname = "Le prénom est requis";
        }

        // Validation de l'email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email_sender.trim()) {
            newErrors.email_sender = "L'email est requis";
        } else if (!emailRegex.test(formData.email_sender)) {
            newErrors.email_sender = "Format d'email invalide";
        }

        // Validation du message
        if (!formData.message.trim()) {
            newErrors.message = "Le message est requis";
        } else if (formData.message.trim().length < 10) {
            newErrors.message = "Le message doit contenir au moins 10 caractères";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));

        // Effacer l'erreur du champ modifié
        if (errors[name]) {
            setErrors(prevErrors => ({
                ...prevErrors,
                [name]: undefined
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Valider le formulaire avant envoi
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            await sendMail(formData);
            setSubmitStatus({ success: true, message: "Message envoyé avec succès!" });
            // Reset form after successful submission
            setFormData({
                firstname: "",
                lastname: "",
                email_sender: "",
                organization: "",
                message: ""
            });
        } catch (error) {
            setSubmitStatus({
                success: false,
                message: "Une erreur s'est produite lors de l'envoi du message."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // SVG Checkmark icon for success state
    const CheckmarkIcon = () => (
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
        </svg>
    );

    // SVG Error icon for error state
    const ErrorIcon = () => (
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
    );

    return (
        <div className="flex bg-white justify-center w-full">
            <div className="w-full max-w-7xl px-4 sm:px-6 mb-16 relative">
                {/* Alerte style Alertify avec la palette de couleurs personnalisée */}
                {submitStatus && showAlert && (
                    <div
                        className={`fixed top-4 right-4 left-4 md:left-auto md:w-96 z-50 p-4 rounded-md shadow-lg transition-all transform ${showAlert ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
                            } ${submitStatus.success ? 'bg-greenprimary' : 'bg-danger'} text-white`}
                    >
                        <div className="flex items-center">
                            {submitStatus.success ? <CheckmarkIcon /> : <ErrorIcon />}
                            <div className="font-medium">{submitStatus.message}</div>
                            <button
                                onClick={() => setShowAlert(false)}
                                className="ml-auto text-white hover:opacity-80"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                        {/* Barre de progression pour indiquer le temps avant disparition */}
                        <div className="w-full bg-white bg-opacity-30 h-1 mt-2 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-white animate-shrink-width"
                                style={{ animation: "shrink 5s linear forwards" }}
                            ></div>
                        </div>
                    </div>
                )}

                {/* Titre - plus petit sur mobile */}
                <h2 className="text-3xl sm:text-4xl font-semibold text-greenprimary flex items-center">
                    <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenprimary mr-2"></span>
                    Nous contacter
                </h2>

                {/* Formulaire */}
                <form className="mt-4 sm:mt-6" onSubmit={handleSubmit} noValidate>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        {/* Nom */}
                        <div>
                            <label className="block text-blueprimary text-sm sm:text-base">Nom</label>
                            <input
                                type="text"
                                name="lastname"
                                value={formData.lastname}
                                onChange={handleInputChange}
                                placeholder="Votre nom"
                                className={`w-full mt-1 p-3 sm:p-4 bg-inputbackground rounded-md focus:outline-none ${errors.lastname ? 'border-2 border-danger' : ''
                                    }`}
                            />
                            {errors.lastname && (
                                <p className="text-danger text-xs mt-1 flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                    {errors.lastname}
                                </p>
                            )}
                        </div>

                        {/* Prénom */}
                        <div>
                            <label className="block text-blueprimary text-sm sm:text-base">Prénom</label>
                            <input
                                type="text"
                                name="firstname"
                                value={formData.firstname}
                                onChange={handleInputChange}
                                placeholder="Votre prénom"
                                className={`w-full mt-1 p-3 sm:p-4 bg-inputbackground rounded-md focus:outline-none ${errors.firstname ? 'border-2 border-danger' : ''
                                    }`}
                            />
                            {errors.firstname && (
                                <p className="text-danger text-xs mt-1 flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                    {errors.firstname}
                                </p>
                            )}
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-blueprimary text-sm sm:text-base">Email</label>
                            <input
                                type="email"
                                name="email_sender"
                                value={formData.email_sender}
                                onChange={handleInputChange}
                                placeholder="JohnDoe214@gmail.com"
                                className={`w-full mt-1 p-3 sm:p-4 bg-inputbackground rounded-md focus:outline-none ${errors.email_sender ? 'border-2 border-danger' : ''
                                    }`}
                            />
                            {errors.email_sender && (
                                <p className="text-danger text-xs mt-1 flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                    {errors.email_sender}
                                </p>
                            )}
                        </div>

                        {/* Organisme */}
                        <div>
                            <label className="block text-blueprimary text-sm sm:text-base">Organisme</label>
                            <input
                                type="text"
                                name="organization"
                                value={formData.organization}
                                onChange={handleInputChange}
                                placeholder="Votre organisme"
                                className="w-full mt-1 p-3 sm:p-4 bg-inputbackground rounded-md focus:outline-none"
                            />
                        </div>
                    </div>

                    {/* Message */}
                    <div className="mt-3 sm:mt-4">
                        <label className="block text-blueprimary text-sm sm:text-base">Message</label>
                        <textarea
                            rows="3"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Écrire votre message ici"
                            className={`w-full mt-1 p-3 sm:p-4 bg-inputbackground rounded-md focus:outline-none ${errors.message ? 'border-2 border-danger' : ''
                                }`}
                        ></textarea>
                        {errors.message && (
                            <p className="text-danger text-xs mt-1 flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                                {errors.message}
                            </p>
                        )}
                    </div>

                    {/* Bouton Envoyer */}
                    <div className="mt-4 sm:mt-6">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full bg-greenlogo text-white font-bold py-2.5 sm:py-3 rounded-md transition ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-greenprimary'}`}
                        >
                            {isSubmitting ? 'ENVOI EN COURS...' : 'ENVOYER'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContactSection;